// VFR Devis Digital - Sync Store
// Story 6-1: Store Pinia pour gérer la queue de synchronisation

import { defineStore } from 'pinia'

// ============================================
// Types
// ============================================

export type SyncStatus = 'pending' | 'syncing' | 'success' | 'failed'

// Story 6-5: Attempt history for logs
export interface SyncAttempt {
  timestamp: number
  attempt: number
  error?: string
  success: boolean
}

export interface SyncItem {
  id: string
  quoteId: string
  status: SyncStatus
  createdAt: number
  startedAt?: number
  completedAt?: number
  attempts: number
  lastError?: string
  freshId?: string      // ID retourné par Fresh après sync
  freshRef?: string     // Référence devis Fresh
  attemptHistory?: SyncAttempt[]  // Story 6-5: Log history
}

interface SyncState {
  syncQueue: SyncItem[]
  currentSync: SyncItem | null
  lastSyncAt: number | null
  lastError: string | null
}

// ============================================
// Helper Functions
// ============================================

function generateSyncId(): string {
  return `sync-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

// ============================================
// Store Definition
// ============================================

export const useSyncStore = defineStore('sync', {
  // ----------------------------------------
  // State
  // ----------------------------------------
  state: (): SyncState => ({
    syncQueue: [],
    currentSync: null,
    lastSyncAt: null,
    lastError: null
  }),

  // ----------------------------------------
  // Getters
  // ----------------------------------------
  getters: {
    /**
     * AC5: Check if there are pending items
     */
    hasPending(): boolean {
      return this.syncQueue.some(item => item.status === 'pending')
    },

    /**
     * AC5: Count of failed items
     */
    failedCount(): number {
      return this.syncQueue.filter(item => item.status === 'failed').length
    },

    /**
     * AC5: Total queue length
     */
    queueLength(): number {
      return this.syncQueue.length
    },

    /**
     * AC5: Count of pending items
     */
    pendingCount(): number {
      return this.syncQueue.filter(item => item.status === 'pending').length
    },

    /**
     * Check if sync is in progress
     */
    isSyncing(): boolean {
      return this.currentSync !== null
    },

    /**
     * Get item by ID
     */
    getItemById: (state) => {
      return (id: string): SyncItem | undefined => {
        // Check currentSync first
        if (state.currentSync?.id === id) {
          return state.currentSync
        }
        return state.syncQueue.find(item => item.id === id)
      }
    },

    /**
     * Get items by quote ID
     */
    getItemsByQuoteId: (state) => {
      return (quoteId: string): SyncItem[] => {
        const items = state.syncQueue.filter(item => item.quoteId === quoteId)
        if (state.currentSync?.quoteId === quoteId) {
          items.unshift(state.currentSync)
        }
        return items
      }
    },

    // ----------------------------------------
    // Story 7-4: Sync Statistics Getters
    // ----------------------------------------

    /**
     * Story 7-4: Count of successful syncs
     */
    successCount(): number {
      return this.syncQueue.filter(item => item.status === 'success').length
    },

    /**
     * Story 7-4: Success rate as percentage (0-100)
     */
    successRate(): number {
      const total = this.successCount + this.failedCount
      if (total === 0) return 100 // No data = 100%
      return Math.round((this.successCount / total) * 100)
    },

    /**
     * Story 7-4: Average sync time in milliseconds
     */
    avgSyncTime(): number {
      const completedItems = this.syncQueue.filter(
        item => item.status === 'success' && item.startedAt && item.completedAt
      )
      if (completedItems.length === 0) return 0

      const totalTime = completedItems.reduce((sum, item) => {
        return sum + ((item.completedAt || 0) - (item.startedAt || 0))
      }, 0)

      return Math.round(totalTime / completedItems.length)
    },

    /**
     * Story 7-4: Average number of retries before success
     */
    avgRetries(): number {
      const successItems = this.syncQueue.filter(item => item.status === 'success')
      if (successItems.length === 0) return 0

      const totalAttempts = successItems.reduce((sum, item) => sum + item.attempts, 0)
      return Math.round((totalAttempts / successItems.length) * 10) / 10
    },

    /**
     * Story 7-4: Last 5 failed syncs
     */
    recentFailures(): SyncItem[] {
      return [...this.syncQueue]
        .filter(item => item.status === 'failed')
        .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))
        .slice(0, 5)
    }
  },

  // ----------------------------------------
  // Actions
  // ----------------------------------------
  actions: {
    /**
     * AC3: Add a quote to the sync queue
     */
    enqueue(quoteId: string): SyncItem {
      const now = Date.now()

      const newItem: SyncItem = {
        id: generateSyncId(),
        quoteId,
        status: 'pending',
        createdAt: now,
        attempts: 0
      }

      this.syncQueue.push(newItem)

      console.log('[SyncStore] Enqueued quote:', quoteId, 'id:', newItem.id)
      return newItem
    },

    /**
     * AC3: Get next pending item and start syncing (FIFO)
     * Returns null if queue empty or sync already in progress
     */
    dequeue(): SyncItem | null {
      // AC2: Only one sync at a time
      if (this.currentSync !== null) {
        console.log('[SyncStore] Sync already in progress, cannot dequeue')
        return null
      }

      // AC2: FIFO - find first pending item
      const pendingIndex = this.syncQueue.findIndex(item => item.status === 'pending')

      if (pendingIndex === -1) {
        console.log('[SyncStore] No pending items in queue')
        return null
      }

      // Remove from queue
      const [item] = this.syncQueue.splice(pendingIndex, 1)

      // Update item status
      item.status = 'syncing'
      item.startedAt = Date.now()
      item.attempts += 1

      // Set as current sync
      this.currentSync = item

      console.log('[SyncStore] Dequeued and syncing:', item.quoteId, 'attempt:', item.attempts)
      return item
    },

    /**
     * Mark current sync as successful
     */
    markSuccess(freshId?: string, freshRef?: string): void {
      if (!this.currentSync) {
        console.warn('[SyncStore] No current sync to mark as success')
        return
      }

      const now = Date.now()

      // Story 6-5: Record attempt history
      if (!this.currentSync.attemptHistory) {
        this.currentSync.attemptHistory = []
      }
      this.currentSync.attemptHistory.push({
        timestamp: now,
        attempt: this.currentSync.attempts,
        success: true
      })

      // Update item
      this.currentSync.status = 'success'
      this.currentSync.completedAt = now
      if (freshId) this.currentSync.freshId = freshId
      if (freshRef) this.currentSync.freshRef = freshRef

      console.log('[SyncStore] Sync success:', this.currentSync.quoteId, 'freshId:', freshId)

      // Update store state
      this.lastSyncAt = now
      this.currentSync = null
    },

    /**
     * Mark current sync as failed and return to queue
     */
    markFailed(error: string): void {
      if (!this.currentSync) {
        console.warn('[SyncStore] No current sync to mark as failed')
        return
      }

      const now = Date.now()

      // Story 6-5: Record attempt history
      if (!this.currentSync.attemptHistory) {
        this.currentSync.attemptHistory = []
      }
      this.currentSync.attemptHistory.push({
        timestamp: now,
        attempt: this.currentSync.attempts,
        error,
        success: false
      })

      // Update item
      this.currentSync.status = 'failed'
      this.currentSync.completedAt = now
      this.currentSync.lastError = error

      console.log('[SyncStore] Sync failed:', this.currentSync.quoteId, 'error:', error)

      // Put back in queue with failed status
      this.syncQueue.push(this.currentSync)

      // Update store state
      this.lastError = error
      this.currentSync = null
    },

    /**
     * AC3: Retry a failed item (put back to pending)
     */
    retry(itemId: string): boolean {
      const item = this.syncQueue.find(i => i.id === itemId)

      if (!item) {
        console.warn('[SyncStore] Item not found for retry:', itemId)
        return false
      }

      if (item.status !== 'failed') {
        console.warn('[SyncStore] Item is not failed, cannot retry:', itemId)
        return false
      }

      item.status = 'pending'
      item.lastError = undefined

      console.log('[SyncStore] Item marked for retry:', itemId)
      return true
    },

    /**
     * AC3: Remove all failed items from queue
     */
    clearFailed(): number {
      const initialLength = this.syncQueue.length
      this.syncQueue = this.syncQueue.filter(item => item.status !== 'failed')
      const removedCount = initialLength - this.syncQueue.length

      console.log('[SyncStore] Cleared failed items:', removedCount)
      return removedCount
    },

    /**
     * Clear last error
     */
    clearError(): void {
      this.lastError = null
    },

    /**
     * Reset store (for testing)
     */
    $reset(): void {
      this.syncQueue = []
      this.currentSync = null
      this.lastSyncAt = null
      this.lastError = null
    }
  },

  // ----------------------------------------
  // AC4: Persistence
  // ----------------------------------------
  persist: {
    key: 'vfr-sync',
    storage: localStorage,
    paths: ['syncQueue', 'lastSyncAt', 'lastError'],
    afterRestore: (ctx) => {
      console.log('[SyncStore] Restored from localStorage:', ctx.store.syncQueue.length, 'items')

      // Reset any items that were "syncing" when app crashed
      ctx.store.syncQueue.forEach((item: SyncItem) => {
        if (item.status === 'syncing') {
          item.status = 'pending'
          console.log('[SyncStore] Reset interrupted sync to pending:', item.quoteId)
        }
      })
    }
  }
})

export default useSyncStore
